import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { BookmarkCheck, Bookmark, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';

/**
 * iter95 — DocTemplatePicker
 * iter108 — adds default-per-type star
 */
export const DocTemplatePicker = ({ api, docType, appliesToType, collectCurrent, onApply, canEdit = true, testIdPrefix = 'tpl' }) => {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState('');

  const load = async () => {
    try {
      const params = new URLSearchParams({ doc_type: docType });
      if (appliesToType) params.append('applies_to_type', appliesToType);
      const res = await api().get(`/doc-templates?${params.toString()}`);
      setTemplates(res.data || []);
    } catch { /* silent — templates are optional */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [docType, appliesToType]);

  const applyTemplate = (id) => {
    setSelected(id);
    const t = templates.find(x => x.id === id);
    if (t && onApply) {
      onApply(t.template_data || {});
      toast.success(`Loaded template "${t.name}"`);
    }
  };

  const save = async () => {
    const trimmed = (name || '').trim();
    if (!trimmed) { toast.error('Template name required'); return; }
    setSaving(true);
    try {
      const template_data = collectCurrent ? (collectCurrent() || {}) : {};
      await api().post('/doc-templates', {
        name: trimmed,
        doc_type: docType,
        template_data,
        applies_to_type: appliesToType || null,
        is_default: !!saveAsDefault,
      });
      toast.success(`Saved template "${trimmed}"${saveAsDefault ? ' (default)' : ''}`);
      setOpen(false); setName(''); setSaveAsDefault(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const toggleDefault = async (t) => {
    try {
      await api().patch(`/doc-templates/${t.id}`, { is_default: !t.is_default });
      toast.success(t.is_default ? 'Removed as default' : 'Set as default');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id, nm) => {
    if (!window.confirm(`Delete template "${nm}"?`)) return;
    try { await api().delete(`/doc-templates/${id}`); toast.success('Deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-root`}>
      {templates.length > 0 && (
        <>
          <Select value={selected} onValueChange={applyTemplate}>
            <SelectTrigger className="h-8 w-56 rounded-sm text-xs" data-testid={`${testIdPrefix}-select`}><SelectValue placeholder="Load template…" /></SelectTrigger>
            <SelectContent>
              {templates.map(t => (
                <div key={t.id} className="flex items-center justify-between pr-1">
                  <SelectItem value={t.id} data-testid={`${testIdPrefix}-opt-${t.id}`}>
                    <span className="flex items-center gap-1">
                      {t.is_default && <Star className="w-3 h-3 fill-amber-400 text-amber-500" />}
                      {t.name}
                    </span>
                  </SelectItem>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <button
                        className={`${t.is_default ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'} px-1`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDefault(t); }}
                        title={t.is_default ? 'Default — click to unpin' : 'Pin as default for this type'}
                        data-testid={`${testIdPrefix}-star-${t.id}`}
                      ><Star className={`w-3 h-3 ${t.is_default ? 'fill-amber-400' : ''}`} /></button>
                      <button className="text-slate-400 hover:text-red-600 px-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(t.id, t.name); }} data-testid={`${testIdPrefix}-del-${t.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </SelectContent>
          </Select>
        </>
      )}
      {canEdit && (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-sm" data-testid={`${testIdPrefix}-save-btn`}>
          <Bookmark className="w-3.5 h-3.5 mr-1" /> Save as template
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testIdPrefix}-save-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookmarkCheck className="w-4 h-4" /> Save as template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Template name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Acme — NET30, FedEx Ground" data-testid={`${testIdPrefix}-name-input`} />
            </div>
            {appliesToType && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={saveAsDefault} onCheckedChange={(v) => setSaveAsDefault(!!v)} data-testid={`${testIdPrefix}-default-check`} />
                <span>Pin as the default for <strong>{appliesToType.replace(/_/g, ' ')}</strong> — new WOs of this type will auto-apply it.</span>
              </label>
            )}
            <p className="text-xs text-slate-500">Current form values will be saved. Line items (if any) are included for Work Orders.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`${testIdPrefix}-confirm-save-btn`}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocTemplatePicker;
